import {pickPhysicalUdoAdditionalColumnValues} from "~/utilities/capabilityUdoTemplateColumns";
import {useUserDefinedObjectFieldValidation} from "~/components/userDefinedObjectAdditionalColumnField";
import type {UserDefinedObjectTemplateAdditionalColumn as UdoColumn} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import InformationCircleIcon from "@atlaskit/icon/core/information-circle";
import CalendarIcon from "@atlaskit/icon/core/calendar";
import NoteIcon from "@atlaskit/icon/core/note";
import SaveIcon from "@atlaskit/icon-lab/core/save";
import {token} from "@atlaskit/tokens";
import {useQueryClient} from "@tanstack/react-query";
import {useEffect, useMemo, useRef, useState} from "react";
import {AnankeTempCollapsibleSectionWithIcon} from "~/components/anankeTemp/anankeTempCollapsibleSectionWithIcon";
import {AnankeTempVerticalSpacer} from "~/components/anankeTemp/anankeTempVerticalSpacer";
import {EventContainerEditPanelColumns, type ColumnGroupWithOptionalIcon} from "~/components/eventContainerDetails/eventContainerDetailsColumns";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {eventContainerStatusToDisplayName} from "~/utilities/domainStatusDisplayNames";
import type {
    EventContainerConstraint as Constraint,
    EventContainerTemplate as EventContainerTemplateConfiguration,
} from "~/domainModels/workspaceModules/eventContainersModule";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeHeading} from "@vesta/ananke/components/anankeHeading";
import {AnankeSimpleTag} from "@vesta/ananke/components/anankeSimpleTag";
import {AnankeAvatar} from "@vesta/ananke/components/anankeAvatar";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventContainerWithEvents} from "~/utilities/apis/api";
import {invalidateOttopilotDataViewerQueries} from "~/components/ottopilotDataViewer/invalidateOttopilotDataViewerQueries";
import {EVENT_CONTAINERS_MODULE_DATA_VIEWER_QUERY_KEY} from "~/utilities/archiveEventContainerWithEvents";
import {invalidateConfigDrivenWorkspaceQueries} from "~/workspaceConfig/invalidateConfigDrivenWorkspaceQueries";
import {useReportPanelDirty} from "~/components/ottopilotDataViewer/rowEngagement/rowEngagementUnsavedChangesContext";
import {isFormValueDirty} from "~/utilities/isFormValueDirty";
import {amaronUpdateEventContainerProfileAction} from "~/utilities/apis/amaronActionsApi";
import {duluxUpdateEventContainerProfileAction} from "~/utilities/apis/duluxActionsApi";
import {updateEventContainer} from "~/utilities/apis/eventContainersApi";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import {formatDateTime, getUserDisplayName} from "~/utilities/utilities";
import {
    isBobcardWorkspace,
    mergeBobcardJourneyAdditionalColumnValuesWithApplicationStatusWhenApplicationStageChanges as mergeBobcardEventContainerAdditionalColumnValuesWithApplicationStatusWhenApplicationStageChanges,
} from "~/domainModels/workspaceSpecific/bobcard";
import {
    AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID,
    AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID,
    AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID,
    AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    isAmaronWorkspace,
} from "~/domainModels/workspaceSpecific/amaron";
import {resolveEventContainerEditPanelGlobalConstraints} from "~/utilities/eventContainerGlobalConstraints";
import {getEditPanelColumnIsEditable} from "~/domainModels/workspaceModules/eventContainerEditPanelConstraints";
import {eventContainerEditPanelIconsMap} from "~/domainModels/eventContainerPanelIcons";

import {useUsers} from "~/contexts/workspaceUsersContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {
    getAmaronEnquiryEvent,
    getEmotoradLifecycleOverviewHeaderRawStatus,
    isInQueueEventContainerAssignmentOwnerSet,
    eventContainerInQueueHeaderBadgeColors,
    resolveAmaronEnquiryHeaderStatus,
    resolveAmaronLeadsHeaderStatus,
    resolveEmotoradLifecycleOverviewHeaderPlatformDisplay,
    resolveEmotoradLifecycleOverviewHeaderStatus,
    type EventContainerInQueueHeaderBadgeKind,
} from "~/components/eventContainerComponents/eventContainerViewPanel";
import {
    EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID,
    isEmotoradWorkspace,
} from "~/domainModels/workspaceSpecific/emotorad";
import {
    DULUX__EVENT_CONTAINER__APPOINTMENT_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CALL_LOG_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CITY__COLUMN_ID,
    DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ENQUIRY_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LAST_EVENT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LAST_EVENT_TIMESTAMP__COLUMN_ID,
    DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID,
    DULUX__EVENT_CONTAINER__ONBOARDING_COUNT__COLUMN_ID,
    DULUX__EVENT_CONTAINER__PINCODE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__PLATFORM__COLUMN_ID,
    DULUX__EVENT_CONTAINER__REMARKS__COLUMN_ID,
    DULUX__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__SCHEDULED_CALL_TIME__COLUMN_ID,
    DULUX__EVENT_CONTAINER__SOURCE__COLUMN_ID,
    DULUX__EVENT_CONTAINER__STATE__COLUMN_ID,
    isDuluxWorkspace,
    resolveDuluxJourneyGlobalConstraints as resolveDuluxEventContainerGlobalConstraints,
} from "~/domainModels/workspaceSpecific/dulux";
import {getGeographyDetailsByPincode, type GeographyDetailsResult} from "~/utilities/apis/geography";
import {ListNotesPanelBody} from "~/components/notes/listNotesSidePanel";
import {
    applyAmaronAssignedAgentReadOnlyPolicy,
    isAmaronAgentUser,
    isAmaronJourneyAssignedAgentSet as isAmaronEventContainerAssignedAgentSet,
} from "~/utilities/workspaceSpecific/amaronAgentAssignment";
import {
    getEventContainerOrganizationalUnitColumnId,
    getEventContainerOrganizationalUnitId,
} from "~/utilities/ottopilotSemanticColumns";

const FALLBACK_EDIT_PANEL_GROUP_ID = "dd9e0dfe-835b-4f65-b319-13fd044b9cb5" as Uuid;

function unknownToPanelText(value: unknown): string {
    if (value === null || value === undefined) {
        return "";
    }
    if (typeof value === "string") {
        return value;
    }
    if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
        return `${value}`;
    }
    if (Array.isArray(value)) {
        return value
            .map((item) => unknownToPanelText(item))
            .filter((item) => item.trim() !== "")
            .join(", ");
    }
    try {
        return JSON.stringify(value);
    } catch {
        return "";
    }
}

function getEditPanelColumnGroupingFromConfig(getAdditionalColumns: CapabilityUdoColumnResolver,
    config: EventContainerTemplateConfiguration,
    eventContainer: EventContainerWithEvents,
    resolvedGlobalConstraints?: Array<Constraint>,
): Array<ColumnGroupWithOptionalIcon> {
    const editPanel = config.configuration.eventContainersPage.editPanel;
    const baseSectionClassName = "py-ds-space-100 px-ds-space-200";
    const firstSectionClassName = `border-t border-ds-border ${baseSectionClassName}`;
    const nextSectionClassName = `border-t border-ds-border ${baseSectionClassName}`;

    if (editPanel === undefined || editPanel.columnGrouping.length === 0) {
        return [
            {
                id: FALLBACK_EDIT_PANEL_GROUP_ID,
                displayName: "Journey details",
                icon: InformationCircleIcon,
                iconColor: "var(--ds-icon-accent-blue)" as ColumnGroupWithOptionalIcon["iconColor"],
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                className: firstSectionClassName,
                columns: getAdditionalColumns(config).map((col) => ({
                    id: col.id,
                    columnId: col.id,
                    columnType: "OttopilotEventContainer" as const,
                    isAdditionalColumn: true,
                    isEditable: true,
                    label: col.displayName,
                })),
            },
        ];
    }

    const globalConstraints = resolvedGlobalConstraints ?? editPanel.globalConstraints;

    return editPanel.columnGrouping.map((group, index) => {
        const icon = group.iconId !== undefined ? eventContainerEditPanelIconsMap[group.iconId] : undefined;
        const columns = group.columns.map((col) => ({
            id: col.id,
            columnId: col.columnId,
            columnType: col.columnType,
            eventTemplateId: col.columnType === "OttopilotEvent" ? col.eventTemplateId : undefined,
            isAdditionalColumn: col.isAdditionalColumn,
            isEditable: getEditPanelColumnIsEditable(
                eventContainer,
                {
                    isEditable: col.isEditable,
                    constraints: col.constraints,
                    columnType: col.columnType,
                    columnId: col.columnId,
                    isAdditionalColumn: col.isAdditionalColumn,
                    eventTemplateId: col.columnType === "OttopilotEvent" ? col.eventTemplateId : undefined,
                },
                undefined,
                globalConstraints,
            ),
            isRequired: (col as {isRequired?: boolean}).isRequired === true,
            label: col.label,
        }));
        return {
            id: group.id,
            displayName: group.displayName,
            icon: icon,
            iconColor: group.iconColor as ColumnGroupWithOptionalIcon["iconColor"],
            iconBackgroundColor: group.iconBackgroundColor,
            initialIsOpen: group.initialIsOpen,
            className: group.className ?? (index === 0 ? firstSectionClassName : nextSectionClassName),
            columns: columns,
        };
    }) as Array<ColumnGroupWithOptionalIcon>;
}

/**
 * InQueue (Bobcard): only Lead Owner can be edited, and only while the lead is unassigned (no owner yet).
 */
function applyBobcardInQueueEditPanelPolicy(
    columnGrouping: Array<ColumnGroupWithOptionalIcon>,
    eventContainer: EventContainerWithEvents,
    leadOwnerColumnId: Uuid,
): Array<ColumnGroupWithOptionalIcon> {
    const leadOwnerRaw = eventContainer.additionalColumnValues[leadOwnerColumnId];
    const isUnassigned = leadOwnerRaw === null || leadOwnerRaw === undefined || (typeof leadOwnerRaw === "string" && leadOwnerRaw === "");
    return columnGrouping.map((group) => ({
        ...group,
        columns: group.columns.map((col) => {
            const isLeadOwnerColumn = col.columnId === leadOwnerColumnId;
            return {
                ...col,
                isEditable: isLeadOwnerColumn ? isUnassigned : false,
            };
        }),
    }));
}

function normalizeEmotoradLifecycleOwnerDisplayLabel(value: string): string {
    return value.trim().toLowerCase();
}

function resolveEmotoradInQueueLifecycleOwnerColumnIdFromConfig(additionalColumns: ReadonlyArray<UdoColumn>): Uuid | null {
    const column = additionalColumns.find((item) =>
        ["lifecycle owner", "lead owner", "action owner"].includes(normalizeEmotoradLifecycleOwnerDisplayLabel(item.displayName)),
    );
    return column?.id ?? null;
}

function isEmotoradInQueueLifecycleOwnerUnassigned(eventContainer: EventContainerWithEvents, lifecycleOwnerColumnId: Uuid | null): boolean {
    if (lifecycleOwnerColumnId === null) {
        return true;
    }
    const raw = eventContainer.additionalColumnValues[lifecycleOwnerColumnId];
    return raw === null || raw === undefined || (typeof raw === "string" && raw.trim() === "");
}

/**
 * Emotorad In Queue: only Assigned Dealer is editable in Showroom Details, and only while lifecycle owner is unassigned.
 */
function buildEmotoradLifecycleCustomerGeographyPatch(details: GeographyDetailsResult | null): Record<string, unknown> {
    if (details === null) {
        return {
            [EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID]: null,
            [EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID]: null,
            [EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID]: null,
        };
    }

    const patch: Record<string, unknown> = {
        [EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID]: details.state ?? null,
        [EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID]: details.city ?? null,
    };
    patch[EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID] = details.zone ?? null;
    return patch;
}

const EMOTORAD_LIFECYCLE_READ_ONLY_CUSTOMER_FIELD_COLUMN_IDS = new Set<string>([
    String(EMOTORAD__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID),
    String(EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID),
    String(EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID),
    String(EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID),
]);

/** Emotorad Lifecycle Overview: customer number and geography are system-derived — never editable in the table edit panel. */
function applyEmotoradLifecycleReadOnlyCustomerFieldsPolicy(columnGrouping: Array<ColumnGroupWithOptionalIcon>): Array<ColumnGroupWithOptionalIcon> {
    return columnGrouping.map((group) => ({
        ...group,
        columns: group.columns.map((col) => {
            if (EMOTORAD_LIFECYCLE_READ_ONLY_CUSTOMER_FIELD_COLUMN_IDS.has(String(col.columnId)) === true) {
                return {...col, isEditable: false};
            }
            return col;
        }),
    }));
}

const DULUX_LEADS_READ_ONLY_COLUMN_IDS = new Set<string>([
    String(DULUX__EVENT_CONTAINER__CONTACT_NUMBER__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__REMARKS__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__PINCODE__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__STATE__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__CITY__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__LAST_EVENT__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__LAST_EVENT_TIMESTAMP__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__ASSIGNED_ON__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__SOURCE__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__PLATFORM__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__SCHEDULED_CALL_DATE__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__SCHEDULED_CALL_TIME__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__ENQUIRY_COUNT__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__CALL_LOG_COUNT__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__APPOINTMENT_COUNT__COLUMN_ID),
    String(DULUX__EVENT_CONTAINER__ONBOARDING_COUNT__COLUMN_ID),
]);

function applyDuluxLeadsReadOnlyLeadStatusEditPanelPolicy(columnGrouping: Array<ColumnGroupWithOptionalIcon>): Array<ColumnGroupWithOptionalIcon> {
    return columnGrouping.map((group) => ({
        ...group,
        columns: group.columns.map((col) => {
            if (DULUX_LEADS_READ_ONLY_COLUMN_IDS.has(String(col.columnId)) === true) {
                return {...col, isEditable: false};
            }
            return col;
        }),
    }));
}

const AMARON_IN_QUEUE_EDITABLE_COLUMN_IDS = new Set<string>([
    String(AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID),
    String(AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID),
]);

/**
 * Amaron In Queue only: Customer Name and Assigned Agent may be edited.
 * Assigned Agent is read-only when the eventContainer was already assigned on open (saved state),
 * not when the user is picking an agent in the current edit session.
 */
function applyAmaronInQueueEditPanelPolicy(
    columnGrouping: Array<ColumnGroupWithOptionalIcon>,
    savedEventContainer: EventContainerWithEvents,
): Array<ColumnGroupWithOptionalIcon> {
    const assignedAgentLocked = isAmaronEventContainerAssignedAgentSet(savedEventContainer.additionalColumnValues as Record<string, unknown>);
    const assignedToColumnId = String(AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID);

    return columnGrouping.map((group) => ({
        ...group,
        columns: group.columns.map((col) => {
            const columnId = String(col.columnId);
            if (AMARON_IN_QUEUE_EDITABLE_COLUMN_IDS.has(columnId) !== true) {
                return {...col, isEditable: false};
            }
            if (columnId === assignedToColumnId) {
                return {...col, isEditable: assignedAgentLocked !== true};
            }
            return col;
        }),
    }));
}

/** My Day Plan edit panel: hide read-only fields (global constraints may still disable some editable fields). */
function filterEditableColumnsOnly(columnGrouping: Array<ColumnGroupWithOptionalIcon>): Array<ColumnGroupWithOptionalIcon> {
    return columnGrouping
        .map((group) => ({
            ...group,
            columns: group.columns.filter((col) => col.isEditable === true),
        }))
        .filter((group) => group.columns.length > 0);
}

function applyEmotoradInQueueAssignedDealerEditPolicy(getAdditionalColumns: CapabilityUdoColumnResolver,
    columnGrouping: Array<ColumnGroupWithOptionalIcon>,
    eventContainer: EventContainerWithEvents,
    eventContainerTemplate: EventContainerTemplateConfiguration,
): Array<ColumnGroupWithOptionalIcon> {
    const lifecycleOwnerColumnId = resolveEmotoradInQueueLifecycleOwnerColumnIdFromConfig(getAdditionalColumns(eventContainerTemplate));
    const unassigned = isEmotoradInQueueLifecycleOwnerUnassigned(eventContainer, lifecycleOwnerColumnId);
    const assignedDealerColumnId = String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID);

    return columnGrouping.map((group) => ({
        ...group,
        columns: group.columns.map((col) => {
            if (String(col.columnId) === assignedDealerColumnId) {
                return {...col, isEditable: unassigned};
            }
            return col;
        }),
    }));
}

export function EventContainerEditPanel(props: {
    row: EventContainerWithEvents;
    isExpanded: boolean;
    eventContainerTemplate: EventContainerTemplateConfiguration;
    eventTemplates: Array<OttopilotEventTemplate>;
    notesEventContainerSourceLabel?: string;
    hideNotesSection?: boolean;
    useInQueueEditLayout?: boolean;
    /** My Day Plan: same scaffold header as view panel; skips In Queue edit field restrictions. */
    useDayPlanEditLayout?: boolean;
    /** Dulux Leads: use the richer assignment header without enabling In Queue edit policies. */
    useDuluxHeaderStyle?: boolean;
    fieldGridColumnCount?: number;
    /** Emotorad Lifecycle Overview: header chip shows eventContainer status (Live / Converted / Churned) only. */
    useEmotoradLifecycleOverviewStatusHeader?: boolean;
    /** Amaron Leads: header chip shows lead status (Live / Converted / Churned / Archived). */
    useAmaronLeadsStatusHeader?: boolean;
    /** Only columns explicitly marked required show the red asterisk. */
    useRequiredAsteriskOnly?: boolean;
    /** Amaron Day Plan: header chip shows enquiry status (Open / Engaged / Non-Engaged). */
    useAmaronEnquiryStatusHeader?: boolean;
    hideAssigneeBar?: boolean;
    /** When set (Bobcard In Queue), only this column may be edited, and only when no lead owner is assigned. */
    bobcardInQueueLeadOwnerColumnId?: Uuid;
    /** Emotorad: lifecycle owner column used for dealer-scoped owner select (In Queue / Lifecycle Overview). */
    emotoradLifecycleOwnerColumnId?: Uuid;
    /** In Queue assignment chip; must match table/kanban (lead owner column, not AON). */
    resolveInQueueAssignmentLabel?: (eventContainer: EventContainerWithEvents) => "Assigned" | "Unassigned";
    inQueueAssignmentOwnerColumnId?: Uuid | null;
    /** Called after a successful save so the parent can dismiss the edit panel. */
    onSaveSuccess?: () => void;
}) {
    const {fieldsRef, validateFields} = useUserDefinedObjectFieldValidation();
    const getAdditionalColumns = useCapabilityUdoColumns();
    const {
        row,
        isExpanded,
        eventContainerTemplate,
        eventTemplates,
        notesEventContainerSourceLabel,
        hideNotesSection = false,
        useInQueueEditLayout = false,
        useDayPlanEditLayout = false,
        useDuluxHeaderStyle = false,
        fieldGridColumnCount,
        useEmotoradLifecycleOverviewStatusHeader = false,
        useAmaronLeadsStatusHeader = false,
        useRequiredAsteriskOnly = false,
        useAmaronEnquiryStatusHeader = false,
        hideAssigneeBar = false,
        bobcardInQueueLeadOwnerColumnId,
        emotoradLifecycleOwnerColumnId,
        resolveInQueueAssignmentLabel,
        inQueueAssignmentOwnerColumnId = null,
        onSaveSuccess,
    } = props;
    const resolvedNotesEventContainerSourceLabel =
        notesEventContainerSourceLabel ?? eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase;

    // columnGrouping accepted for same API as EventContainerEditPanel but not used
    const anankeFlags = useAnankeFlags();
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser, workspaceUser} = useWorkspaceUser();
    const {workspace, workspaceCapabilities, platformUserDetails} = useWorkspace();
    const queryClient = useQueryClient();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [eventContainerWithDiff, setEventContainerWithDiff] = useState<EventContainerWithEvents>(row);
    const emotoradLifecycleGeoFetchGenerationRef = useRef(0);
    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail])),
        [platformUserDetails],
    );

    useEffect(() => {
        setEventContainerWithDiff(row);
        setIsSubmitting(false);
        emotoradLifecycleGeoFetchGenerationRef.current += 1;
    }, [row]);
    const emotoradLifecycleOwnerEditColumnId =
        isEmotoradWorkspace(workspace) === true ? (emotoradLifecycleOwnerColumnId ?? EMOTORAD__EVENT_CONTAINER__LEAD_OWNER__COLUMN_ID) : undefined;
    const inQueueHeaderData = useMemo(() => {
        if (useInQueueEditLayout !== true && useDayPlanEditLayout !== true && useDuluxHeaderStyle !== true) {
            return null;
        }
        const byDisplayName = new Map(getAdditionalColumns(eventContainerTemplate).map((column) => [column.displayName.trim().toLowerCase(), column.id]));
        const resolveUserDisplayName = (value: unknown): string | null => {
            if (value === null || value === undefined) {
                return null;
            }
            const rawValue = String(value).trim();
            if (rawValue === "") {
                return null;
            }
            const displayName = getUserDisplayName(rawValue as Uuid, users, userTemplates, platformUserDetailsByUserId);
            return displayName === "-" ? null : displayName;
        };
        const getAdditionalValue = (...displayNames: string[]) => {
            for (const displayName of displayNames) {
                const id = byDisplayName.get(displayName.trim().toLowerCase());
                if (id === undefined) {
                    continue;
                }
                const value = eventContainerWithDiff.additionalColumnValues[id];
                const displayValue = unknownToPanelText(value);
                if (displayValue.trim() === "") {
                    continue;
                }
                const columnDefinition = getAdditionalColumns(eventContainerTemplate).find((column) => column.id === id);
                if (columnDefinition?.type === UdoColumnType.user) {
                    return resolveUserDisplayName(value) ?? displayValue;
                }
                return resolveUserDisplayName(value) ?? displayValue;
            }
            return "";
        };
        const assignmentOwnerDisplay = getAdditionalValue("Lifecycle Owner", "Lead Owner", "Assigned To", "Assigned Agent");
        const resolveAssigneeNameFromOwnerColumn = (): string => {
            if (inQueueAssignmentOwnerColumnId === null) {
                return assignmentOwnerDisplay === "" ? "-" : assignmentOwnerDisplay;
            }
            const ownerRaw = eventContainerWithDiff.additionalColumnValues[inQueueAssignmentOwnerColumnId];
            if (isInQueueEventContainerAssignmentOwnerSet(ownerRaw) !== true) {
                return "-";
            }
            const matchingColumn = getAdditionalColumns(eventContainerTemplate).find((column) => column.id === inQueueAssignmentOwnerColumnId);
            if (matchingColumn?.type === UdoColumnType.user) {
                return resolveUserDisplayName(ownerRaw) ?? "-";
            }
            return resolveUserDisplayName(ownerRaw) ?? String(ownerRaw);
        };
        const platform = getAdditionalValue("Action Platform", "Platform", "Utm_platform", "utm_platform");

        if (useAmaronLeadsStatusHeader === true) {
            const rawStatus = eventContainerWithDiff.additionalColumnValues[AMARON__EVENT_CONTAINER__LEAD_STATUS__COLUMN_ID];
            const {statusText, statusBadgeKind} = resolveAmaronLeadsHeaderStatus(rawStatus);
            return {
                statusText,
                statusBadgeKind,
                customerName: getAdditionalValue("Customer Name", "customer name", "User Name", "user name") || "-",
                assigneeName: assignmentOwnerDisplay === "" ? "-" : assignmentOwnerDisplay,
                dateText: formatDateTime(row.createdAt),
                platform: platform === "" ? "-" : platform,
            };
        }

        if (useAmaronEnquiryStatusHeader === true) {
            const enquiryEvent = getAmaronEnquiryEvent(eventContainerWithDiff);
            const rawStatus = enquiryEvent?.additionalColumnValues[AMARON__EVENT__ENQUIRY__ENQUIRY_STATUS__COLUMN_ID];
            const {statusText, statusBadgeKind} = resolveAmaronEnquiryHeaderStatus(rawStatus);
            const platformDisplay = resolveEmotoradLifecycleOverviewHeaderPlatformDisplay({
                platformFromDisplayNameLookup: platform,
                additionalColumnValues: eventContainerWithDiff.additionalColumnValues,
            });
            return {
                statusText,
                statusBadgeKind,
                customerName: getAdditionalValue("Customer Name", "customer name", "User Name", "user name") || "-",
                assigneeName: assignmentOwnerDisplay === "" ? "-" : assignmentOwnerDisplay,
                dateText: formatDateTime(row.createdAt),
                platform: platformDisplay,
            };
        }

        if (useEmotoradLifecycleOverviewStatusHeader === true) {
            const rawStatus = getEmotoradLifecycleOverviewHeaderRawStatus(eventContainerWithDiff, eventContainerTemplate);
            const {statusText, statusBadgeKind} = resolveEmotoradLifecycleOverviewHeaderStatus(rawStatus);
            const platformDisplay = resolveEmotoradLifecycleOverviewHeaderPlatformDisplay({
                platformFromDisplayNameLookup: platform,
                additionalColumnValues: eventContainerWithDiff.additionalColumnValues,
            });
            return {
                statusText,
                statusBadgeKind,
                customerName: getAdditionalValue("Customer Name", "customer name", "User Name", "user name") || "-",
                assigneeName: assignmentOwnerDisplay === "" ? "-" : assignmentOwnerDisplay,
                dateText: formatDateTime(row.createdAt),
                platform: platformDisplay,
            };
        }

        const assignmentLabel =
            resolveInQueueAssignmentLabel !== undefined
                ? resolveInQueueAssignmentLabel(eventContainerWithDiff)
                : assignmentOwnerDisplay === ""
                  ? "Unassigned"
                  : "Assigned";
        const platformDisplay = resolveEmotoradLifecycleOverviewHeaderPlatformDisplay({
            platformFromDisplayNameLookup: platform,
            additionalColumnValues: eventContainerWithDiff.additionalColumnValues,
        });
        return {
            statusText: assignmentLabel,
            statusBadgeKind: assignmentLabel.toLowerCase() as EventContainerInQueueHeaderBadgeKind,
            customerName: getAdditionalValue("Customer Name", "customer name", "User Name", "user name") || "-",
            assigneeName: resolveAssigneeNameFromOwnerColumn(),
            dateText: formatDateTime(row.createdAt),
            platform: platformDisplay,
        };
    }, [getAdditionalColumns,
        eventContainerTemplate,
        eventContainerWithDiff,
        eventContainerWithDiff.additionalColumnValues,
        row.createdAt,
        useInQueueEditLayout,
        useDayPlanEditLayout,
        useDuluxHeaderStyle,
        useEmotoradLifecycleOverviewStatusHeader,
        useAmaronLeadsStatusHeader,
        useAmaronEnquiryStatusHeader,
        resolveInQueueAssignmentLabel,
        inQueueAssignmentOwnerColumnId,
        users,
        userTemplates,
        platformUserDetailsByUserId,
    ]);

    const globalConstraints = useMemo(
        () =>
            resolveDuluxEventContainerGlobalConstraints(workspaceCapabilities) ??
            resolveEventContainerEditPanelGlobalConstraints({
                workspace,
                workspaceCapabilities,
                editPanel: eventContainerTemplate.configuration.eventContainersPage.editPanel,
            }),
        [workspace, workspaceCapabilities, eventContainerTemplate.configuration.eventContainersPage.editPanel],
    );

    const columnGrouping = useMemo(() => {
        let next = getEditPanelColumnGroupingFromConfig(getAdditionalColumns, eventContainerTemplate, eventContainerWithDiff, globalConstraints);
        if (bobcardInQueueLeadOwnerColumnId !== undefined) {
            next = applyBobcardInQueueEditPanelPolicy(next, eventContainerWithDiff, bobcardInQueueLeadOwnerColumnId);
        }
        if (
            useInQueueEditLayout === true &&
            useDayPlanEditLayout !== true &&
            isAmaronWorkspace(workspaceCapabilities) === true &&
            useAmaronLeadsStatusHeader !== true
        ) {
            next = applyAmaronInQueueEditPanelPolicy(next, row);
        }
        if (useInQueueEditLayout === true && isEmotoradWorkspace(workspace) === true && bobcardInQueueLeadOwnerColumnId === undefined) {
            next = applyEmotoradInQueueAssignedDealerEditPolicy(getAdditionalColumns, next, eventContainerWithDiff, eventContainerTemplate);
        }
        if (useEmotoradLifecycleOverviewStatusHeader === true && isEmotoradWorkspace(workspace) === true) {
            next = applyEmotoradLifecycleReadOnlyCustomerFieldsPolicy(next);
        }
        if (isDuluxWorkspace(workspaceCapabilities) === true) {
            next = applyDuluxLeadsReadOnlyLeadStatusEditPanelPolicy(next);
        }
        if (
            isAmaronWorkspace(workspaceCapabilities) === true &&
            isAmaronAgentUser(workspaceUser) === true &&
            (useAmaronLeadsStatusHeader === true || useDayPlanEditLayout === true)
        ) {
            next = applyAmaronAssignedAgentReadOnlyPolicy(next);
        }
        if (useDayPlanEditLayout === true) {
            next = filterEditableColumnsOnly(next);
        }
        return next;
    }, [getAdditionalColumns,
        eventContainerTemplate,
        eventContainerWithDiff,
        globalConstraints,
        bobcardInQueueLeadOwnerColumnId,
        useInQueueEditLayout,
        useDayPlanEditLayout,
        useEmotoradLifecycleOverviewStatusHeader,
        useAmaronLeadsStatusHeader,
        row,
        workspace,
        workspaceCapabilities,
        workspaceUser,
    ]);

    // Report unsaved edits to the shared row-engagement guard (no-op outside a panel).
    const [notesDirty, setNotesDirty] = useState(false);
    const columnsDirty = isFormValueDirty(
        eventContainerWithDiff.additionalColumnValues,
        row.additionalColumnValues,
    );
    useReportPanelDirty(columnsDirty || notesDirty);

    const handleColumnValueChange = async (
        columnId: string,
        value: unknown,
        _eventTemplateId: Uuid | undefined, // required by EventContainerDetailsColumns callback signature
    ) => {
        void _eventTemplateId;

        const isLifecycleCustomerPincodeEdit =
            useEmotoradLifecycleOverviewStatusHeader === true &&
            isEmotoradWorkspace(workspace) === true &&
            String(columnId) === String(EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID);

        if (String(columnId) === String(EMOTORAD__EVENT_CONTAINER__ASSIGNED_DEALER_NAME__COLUMN_ID)) {
            const valueText = unknownToPanelText(value);
            const dealerOuId = valueText.trim() === "" ? null : (valueText as Uuid);
            const organizationalUnitColumnId = getEventContainerOrganizationalUnitColumnId(eventContainerTemplate);
            const currentOrganizationalUnitId = eventContainerWithDiff.additionalColumnValues[organizationalUnitColumnId];
            const lifecycleOwnerColumnId = emotoradLifecycleOwnerEditColumnId;
            const nextAdditionalColumnValues: Record<string, unknown> = {
                ...eventContainerWithDiff.additionalColumnValues,
                [columnId]: value,
            };
            if (lifecycleOwnerColumnId !== undefined && dealerOuId !== null && dealerOuId !== currentOrganizationalUnitId) {
                nextAdditionalColumnValues[lifecycleOwnerColumnId] = "";
            }
            if (dealerOuId !== null) {
                nextAdditionalColumnValues[organizationalUnitColumnId] = dealerOuId;
            }
            setEventContainerWithDiff({
                ...eventContainerWithDiff,
                additionalColumnValues: nextAdditionalColumnValues,
            });
            return;
        }

        if (isLifecycleCustomerPincodeEdit !== true) {
            setEventContainerWithDiff({
                ...eventContainerWithDiff,
                additionalColumnValues: {
                    ...eventContainerWithDiff.additionalColumnValues,
                    [columnId]: value,
                },
            });
            return;
        }

        const pincodeText = unknownToPanelText(value).trim();
        setEventContainerWithDiff((prev) => {
            const additionalColumnValues: Record<string, unknown> = {
                ...prev.additionalColumnValues,
                [columnId]: value,
            };
            if (pincodeText === "") {
                Object.assign(additionalColumnValues, buildEmotoradLifecycleCustomerGeographyPatch(null));
            }
            return {
                ...prev,
                additionalColumnValues,
            };
        });

        if (pincodeText === "") {
            return;
        }

        const gen = ++emotoradLifecycleGeoFetchGenerationRef.current;
        try {
            const details = await getGeographyDetailsByPincode({
                intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
                pincode: pincodeText,
                dialCode: "+91",
            });
            if (gen !== emotoradLifecycleGeoFetchGenerationRef.current) {
                return;
            }
            setEventContainerWithDiff((prev) => ({
                ...prev,
                additionalColumnValues: {
                    ...prev.additionalColumnValues,
                    [columnId]: value,
                    ...buildEmotoradLifecycleCustomerGeographyPatch(details),
                },
            }));
        } catch {
            // Geography lookup is best-effort; pincode was already applied above.
        }
    };

    const handleSave = async () => {
        if (!validateFields()) {
            return;
        }
        if (useAmaronLeadsStatusHeader === true) {
            const customerNameRaw = eventContainerWithDiff.additionalColumnValues[AMARON__EVENT_CONTAINER__CUSTOMER_NAME__COLUMN_ID];
            const customerNameText = unknownToPanelText(customerNameRaw).trim();
            if (customerNameText === "") {
                anankeFlags.showFlag({
                    title: "Required fields",
                    description: "Customer Name is required before saving.",
                    appearance: "error",
                    isAutoDismiss: true,
                    autoDismissSeconds: 4,
                });
                return;
            }
        }
        setIsSubmitting(true);
        try {
            let additionalColumnValuesForSave: Record<string, unknown> =
                isBobcardWorkspace(workspace) === true
                    ? mergeBobcardEventContainerAdditionalColumnValuesWithApplicationStatusWhenApplicationStageChanges(
                          eventContainerWithDiff.additionalColumnValues,
                          row.additionalColumnValues,
                      )
                    : eventContainerWithDiff.additionalColumnValues;

            additionalColumnValuesForSave = pickPhysicalUdoAdditionalColumnValues(getAdditionalColumns(eventContainerTemplate), additionalColumnValuesForSave);

            if (isAmaronWorkspace(workspaceCapabilities) === true) {
                if (isAmaronAgentUser(workspaceUser) === true && (useAmaronLeadsStatusHeader === true || useDayPlanEditLayout === true)) {
                    additionalColumnValuesForSave = {
                        ...additionalColumnValuesForSave,
                        [AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID]: row.additionalColumnValues[AMARON__EVENT_CONTAINER__ASSIGNED_TO__COLUMN_ID],
                    };
                }
                const result = await amaronUpdateEventContainerProfileAction({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl: intellsysBaseUrl,
                    request: {
                        workspaceId: workspace.id,
                        eventContainerId: row.id,
                        data: {
                            additionalColumnValues: additionalColumnValuesForSave,
                            organizationalUnitId: getEventContainerOrganizationalUnitId(
                                {additionalColumnValues: additionalColumnValuesForSave},
                                eventContainerTemplate,
                            ),
                        },
                    },
                });

                if (result.success === false) {
                    setIsSubmitting(false);
                    anankeFlags.showFlag({
                        title: "Failed to update eventContainer",
                        description: result.error.message,
                        appearance: "error",
                        autoDismissSeconds: 3,
                        isAutoDismiss: true,
                    });
                    return;
                }

                await Promise.all([
                    invalidateOttopilotDataViewerQueries(queryClient, [EVENT_CONTAINERS_MODULE_DATA_VIEWER_QUERY_KEY]),
                    invalidateConfigDrivenWorkspaceQueries({
                        queryClient,
                        workspaceId: workspace.id,
                    }),
                ]);
                setIsSubmitting(false);
                anankeFlags.showFlag({
                    title: "Event Container updated successfully",
                    appearance: "success",
                    isAutoDismiss: true,
                    autoDismissSeconds: 3,
                });
                onSaveSuccess?.();
                return;
            }

            if (isDuluxWorkspace(workspaceCapabilities) === true) {
                const result = await duluxUpdateEventContainerProfileAction({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl: intellsysBaseUrl,
                    request: {
                        workspaceId: workspace.id,
                        eventContainerId: row.id,
                        additionalColumnValues: additionalColumnValuesForSave,
                        organizationalUnitId: getEventContainerOrganizationalUnitId(
                            {additionalColumnValues: additionalColumnValuesForSave},
                            eventContainerTemplate,
                        ),
                    },
                });

                if (result.success === false) {
                    setIsSubmitting(false);
                    anankeFlags.showFlag({
                        title: "Failed to update eventContainer",
                        description: result.error.message,
                        appearance: "error",
                        autoDismissSeconds: 3,
                        isAutoDismiss: true,
                    });
                    return;
                }

                await Promise.all([
                    invalidateOttopilotDataViewerQueries(queryClient, [EVENT_CONTAINERS_MODULE_DATA_VIEWER_QUERY_KEY]),
                    invalidateConfigDrivenWorkspaceQueries({
                        queryClient,
                        workspaceId: workspace.id,
                    }),
                ]);
                setIsSubmitting(false);
                anankeFlags.showFlag({
                    title: "Event Container updated successfully",
                    appearance: "success",
                    isAutoDismiss: true,
                    autoDismissSeconds: 3,
                });
                onSaveSuccess?.();
                return;
            }

            const result = await updateEventContainer({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                id: row.id,
                templateId: eventContainerTemplate.id,
                data: {
                    additionalColumnValues: additionalColumnValuesForSave,
                },
                workspaceId: workspace.id,
            });

            if (result.success === false) {
                setIsSubmitting(false);
                anankeFlags.showFlag({
                    title: "Failed to update eventContainer",
                    description: result.error.message,
                    appearance: "error",
                    autoDismissSeconds: 3,
                    isAutoDismiss: true,
                });
                return;
            }

            await Promise.all([
                invalidateOttopilotDataViewerQueries(queryClient, [EVENT_CONTAINERS_MODULE_DATA_VIEWER_QUERY_KEY]),
                invalidateConfigDrivenWorkspaceQueries({
                    queryClient,
                    workspaceId: workspace.id,
                }),
            ]);
            setIsSubmitting(false);
            anankeFlags.showFlag({
                title: "Event Container updated successfully",
                appearance: "success",
                isAutoDismiss: true,
                autoDismissSeconds: 3,
            });
            onSaveSuccess?.();
        } catch (err) {
            setIsSubmitting(false);
            anankeFlags.showFlag({
                title: "Failed to update eventContainer",
                description: err instanceof Error ? err.message : "An unexpected error occurred",
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 5,
            });
        }
    };

    const useScaffoldEditHeader = useInQueueEditLayout === true || useDayPlanEditLayout === true || useDuluxHeaderStyle === true;
    const editPanelTitle =
        useDayPlanEditLayout === true
            ? "Edit Lead"
            : useInQueueEditLayout === true
              ? "Edit In Queue"
              : `Edit ${eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase}`;
    const saveButtonLabel = useScaffoldEditHeader === true ? "Update" : "Save changes";

    const notesSection = (
        <AnankeTempCollapsibleSectionWithIcon
            title="Notes"
            icon={NoteIcon}
            iconColor="var(--ds-icon-accent-blue)"
            iconBackgroundColor={token("color.background.accent.teal.subtlest")}
            initialIsOpen={true}
            className="border-t border-ds-border py-ds-space-100 px-ds-space-200"
        >
            <ListNotesPanelBody
                context={{
                    eventContainerId: row.id,
                    eventContainer: eventContainerWithDiff,
                    createScope: {scopeType: "eventContainer"},
                    eventContainerSourceLabel: resolvedNotesEventContainerSourceLabel,
                }}
                eventContainer={eventContainerWithDiff}
                onEventContainerPatched={(patchedEventContainer) => {
                    setEventContainerWithDiff((previous) => ({
                        ...previous,
                        ...patchedEventContainer,
                    }));
                }}
                isFullscreen={false}
                onDirtyChange={setNotesDirty}
            />
        </AnankeTempCollapsibleSectionWithIcon>
    );

    if (isExpanded === true) {
        return (
            <div ref={fieldsRef} className="flex h-full min-h-0 min-w-0 flex-col p-ds-space-200">
                <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto pr-ds-space-050">
                    <div className="grid grid-flow-row gap-y-ds-space-250">
                        <div className="w-full px-ds-space-200 pb-ds-space-150 grid grid-flow-row gap-y-ds-space-100">
                            {useScaffoldEditHeader === true && inQueueHeaderData !== null ? (
                                <>
                                    <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                                        <span
                                            className="inline-flex items-center px-2 py-0.5 text-xs font-medium"
                                            style={{
                                                borderRadius: "3px",
                                                ...(() => {
                                                    const {border, background} = eventContainerInQueueHeaderBadgeColors(inQueueHeaderData.statusBadgeKind);
                                                    return {border: `0.5px solid ${border}`, background};
                                                })(),
                                            }}
                                        >
                                            {inQueueHeaderData.statusText}
                                        </span>
                                    </div>
                                    <div className="min-w-0">
                                        <AnankeHeading size="large">{inQueueHeaderData.customerName}</AnankeHeading>
                                    </div>
                                    <div className="grid max-sm:hidden grid-cols-1 sm:grid-cols-[max-content_max-content] gap-ds-space-100">
                                        <div className="flex h-[28px] min-h-[24px] px-ds-space-150 justify-center items-center rounded-[4px] border border-ds-border bg-ds-background-neutral-subtle gap-ds-space-050">
                                            <CalendarIcon
                                                label="Calendar"
                                                color={token("color.icon.information")}
                                            />
                                            <span>{inQueueHeaderData.dateText}</span>
                                        </div>
                                        <div className="flex h-[28px] min-h-[24px] px-ds-space-150 justify-center items-center rounded-[4px] border border-ds-border bg-ds-background-neutral-subtle">
                                            <span>{inQueueHeaderData.platform}</span>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                                        <AnankeSimpleTag text={eventContainerStatusToDisplayName(row.status)} />
                                        <AnankeSimpleTag
                                            text={eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase}
                                            color="blue"
                                        />
                                    </div>

                                    <div>
                                        <AnankeHeading size="medium">{editPanelTitle}</AnankeHeading>
                                    </div>
                                </>
                            )}
                        </div>

                        <EventContainerEditPanelColumns
                            columnGrouping={columnGrouping}
                            eventContainerTemplate={eventContainerTemplate}
                            eventTemplates={eventTemplates}
                            eventContainerWithDiff={eventContainerWithDiff}
                            eventContainer={row}
                            onColumnValueChange={handleColumnValueChange}
                            useInQueueEditLayout={useInQueueEditLayout}
                            fieldGridColumnCount={fieldGridColumnCount}
                            useDayPlanEditLayout={useDayPlanEditLayout}
                            useAmaronLeadsStatusHeader={useAmaronLeadsStatusHeader}
                            useRequiredAsteriskOnly={useRequiredAsteriskOnly}
                            emotoradLifecycleOwnerColumnId={emotoradLifecycleOwnerEditColumnId}
                            globalConstraints={globalConstraints}
                        />
                        {hideNotesSection !== true ? notesSection : null}
                    </div>
                </div>
                <div className="mt-ds-space-100 -mx-ds-space-200 flex flex-shrink-0 justify-end border-t border-ds-border bg-ds-surface px-ds-space-200 pt-ds-space-150 pb-ds-space-100">
                    <AnankeButton
                        type="button"
                        appearance="primary"
                        onClick={handleSave}
                        isLoading={isSubmitting}
                        iconBefore={useScaffoldEditHeader === true ? SaveIcon : undefined}
                    >
                        {saveButtonLabel}
                    </AnankeButton>
                </div>
            </div>
        );
    } else {
        return (
            <div ref={fieldsRef} className="flex h-full min-h-0 flex-col">
                <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
                    <div className="w-full px-ds-space-200 pt-ds-space-050 pb-ds-space-150 grid grid-flow-row gap-y-ds-space-100">
                        {useScaffoldEditHeader === true && inQueueHeaderData !== null ? (
                            <>
                                <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                                    <span
                                        className="inline-flex items-center px-2 py-0.5 text-xs font-medium"
                                        style={{
                                            borderRadius: "3px",
                                            ...(() => {
                                                const {border, background} = eventContainerInQueueHeaderBadgeColors(inQueueHeaderData.statusBadgeKind);
                                                return {border: `0.5px solid ${border}`, background};
                                            })(),
                                        }}
                                    >
                                        {inQueueHeaderData.statusText}
                                    </span>
                                </div>
                                <div className="min-w-0">
                                    <AnankeHeading size="large">{inQueueHeaderData.customerName}</AnankeHeading>
                                </div>
                                <div className="grid max-sm:hidden grid-cols-1 sm:grid-cols-[max-content_max-content] gap-ds-space-100">
                                    <div className="flex h-[28px] min-h-[24px] px-ds-space-150 justify-center items-center rounded-[4px] border border-ds-border bg-ds-background-neutral-subtle gap-ds-space-050">
                                        <CalendarIcon
                                            label="Calendar"
                                            color={token("color.icon.information")}
                                        />
                                        <span>{inQueueHeaderData.dateText}</span>
                                    </div>
                                    <div className="flex h-[28px] min-h-[24px] px-ds-space-150 justify-center items-center rounded-[4px] border border-ds-border bg-ds-background-neutral-subtle">
                                        <span>{inQueueHeaderData.platform}</span>
                                    </div>
                                </div>
                                {hideAssigneeBar !== true ? (
                                    <div className="flex -mx-ds-space-200 px-ds-space-300 py-ds-space-150 justify-between items-center border-b border-ds-border bg-ds-background-information">
                                        <div className="flex items-center gap-ds-space-100 min-w-0">
                                            <AnankeAvatar
                                                size="small"
                                                name={inQueueHeaderData.assigneeName}
                                            />
                                            <span className="truncate">{inQueueHeaderData.assigneeName}</span>
                                        </div>
                                        <div
                                            className="flex px-ds-space-100 py-ds-space-050 items-center gap-ds-space-075 rounded-[4px]"
                                            style={{background: token("color.icon.inverse")}}
                                        >
                                            <span style={{color: token("color.text.information")}}>Assignee</span>
                                        </div>
                                    </div>
                                ) : null}
                            </>
                        ) : (
                            <>
                                <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                                    <AnankeSimpleTag text={eventContainerStatusToDisplayName(row.status)} />
                                    <AnankeSimpleTag
                                        text={eventContainerTemplate.configuration.moduleDisplayNameVariants.singularSentenceCase}
                                        color="blue"
                                    />
                                </div>

                                <div>
                                    <AnankeHeading size="medium">{editPanelTitle}</AnankeHeading>
                                </div>
                            </>
                        )}
                    </div>

                    {useScaffoldEditHeader !== true && (
                        <AnankeTempVerticalSpacer
                            above="formRow"
                            below="formRow"
                        />
                    )}

                    <div className="w-full grid grid-flow-row">
                        <EventContainerEditPanelColumns
                            columnGrouping={columnGrouping}
                            eventContainerTemplate={eventContainerTemplate}
                            eventTemplates={eventTemplates}
                            eventContainerWithDiff={eventContainerWithDiff}
                            eventContainer={row}
                            onColumnValueChange={handleColumnValueChange}
                            useInQueueEditLayout={useInQueueEditLayout}
                            fieldGridColumnCount={fieldGridColumnCount}
                            useDayPlanEditLayout={useDayPlanEditLayout}
                            useAmaronLeadsStatusHeader={useAmaronLeadsStatusHeader}
                            useRequiredAsteriskOnly={useRequiredAsteriskOnly}
                            emotoradLifecycleOwnerColumnId={emotoradLifecycleOwnerEditColumnId}
                            globalConstraints={globalConstraints}
                        />
                        {hideNotesSection !== true ? notesSection : null}
                    </div>
                </div>

                <div
                    className={
                        useDayPlanEditLayout === true || useInQueueEditLayout === true
                            ? "flex flex-shrink-0 justify-end px-ds-space-200 pt-ds-space-150 pb-ds-space-100 border-t border-ds-border bg-ds-surface"
                            : "flex flex-shrink-0 justify-end pt-ds-space-200 px-ds-space-200 pb-ds-space-100 border-t border-ds-border bg-ds-surface"
                    }
                >
                    <AnankeButton
                        type="button"
                        appearance="primary"
                        onClick={handleSave}
                        isLoading={isSubmitting}
                        iconBefore={useScaffoldEditHeader === true ? SaveIcon : undefined}
                    >
                        {saveButtonLabel}
                    </AnankeButton>
                </div>
            </div>
        );
    }
}
